import * as React from 'react';
import { MouseEvent, useEffect, useState } from 'react';
import { SCurve } from '../BaseComponents/SCurve';
import { Vector } from '../../helpers/Vector';
import { getColorFromDataType } from '../../helpers/getColorFromDataType';
import type { ConnectionValidation, DataConnection, NodeInstance } from '@glueapp/graph-types';
import { ThemeType } from '@/theme/glueTheme';

interface Props {
    connection: DataConnection;
    startPosition: Vector;
    endPosition: Vector;
    theme: ThemeType;
    sendingNodeInstance: NodeInstance | undefined;
    receivingNodeInstance: NodeInstance | undefined;
    onMouseDown: (event: MouseEvent, connection: DataConnection) => void;
    validation?: ConnectionValidation;
    onHoverChange: (connectionId: number, hovered: boolean) => void;
}

const BaseGraphConnection: React.FC<Props> = ({
    connection,
    startPosition,
    endPosition,
    sendingNodeInstance,
    receivingNodeInstance,
    onMouseDown,
    validation,
    onHoverChange,
    theme,
}) => {
    const [hovered, setHovered] = useState<boolean>(false);
    useEffect(() => {
        onHoverChange(connection.id, hovered);
    }, [connection.id, hovered, onHoverChange]);

    // Nodes
    if (!sendingNodeInstance) return null;

    if (!receivingNodeInstance) return null;

    let color;
    if (!validation || validation.valid) {
        switch (connection.sendingPortType) {
            case 'external': {
                const sendingPort = sendingNodeInstance.outPorts.find(port => port.name === connection.sendingPortName);
                if (!sendingPort) return null;
                color = getColorFromDataType(sendingPort.type, theme);
                break;
            }
            case 'inherent': {
                color = theme.primaryContrastColor;
            }
        }
    } else {
        color = 'red';
    }

    return (
        <>
            <SCurve
                startPosition={startPosition}
                endPosition={endPosition}
                strokeColor={color}
                strokeWidth={hovered ? 5 : 2}
                onMouseDown={e => onMouseDown(e, connection)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            />
        </>
    );
};

export const GraphConnection = React.memo(BaseGraphConnection);
